import ApiService from "./api";
import { handleAPIError } from "../utils/apiHelpers";

class ClubMiaAuthService {
  constructor() {
    this.apiService = ApiService;
  }

  /**
   * Register a new Club Mia member
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Registration response
   */
  async register(userData) {
    try {
      // Prepare the data according to the Laravel API requirements
      const registrationData = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        // password: userData.password, // Commented out - may be needed later
        phone: userData.phone,
        identification_number: userData.identification_number,
        identification_type: userData.identification_type,
        birth_date: userData.birthDate || null, // Enviar null si está vacío
        sian_domain: process.env.REACT_APP_SIAN_DOMAIN,
      };

      console.log("Sending registration data:", registrationData);

      const response = await this.apiService.post(
        "/api/V1/club-mia/register",
        registrationData
      );



      if (response.success) {
        console.log("Registration successful:", response.data);

        // Extract the deeply nested data structure: response.data.data.data
        const responseData = response.data?.data?.data || response.data?.data || response.data;

        console.log("Extracted responseData:", responseData);

        // Store token and user data in localStorage (same as login)
        if (responseData.token) {
          localStorage.setItem("clubMiaToken", responseData.token);
          console.log("Token stored:", responseData.token);

          localStorage.setItem(
            "clubMiaUser",
            JSON.stringify(responseData.user || {})
          );
          console.log("User stored:", responseData.user);

          if (responseData.person) {
            localStorage.setItem(
              "clubMiaPerson",
              JSON.stringify(responseData.person)
            );
            console.log("Person stored:", responseData.person);
          }
        }

        return {
          success: true,
          message: response.message || "Registro exitoso",
          data: response.data,
          user: responseData.user || {},
          person: responseData.person || {},
          token: responseData.token || null,
        };
      } else {
        console.error("Registration failed:", response);
        // La estructura real de la respuesta de error es:
        // response.error.data.errors (donde están los errores de validación)
        const validationErrors = response.error?.data?.errors || {};

        console.log("Validation errors found:", validationErrors);
        console.log("Response structure:", {
          message: response.message,
          error: response.error,
          errorData: response.error?.data,
          errorDataErrors: response.error?.data?.errors,
        });

        let errorMessage =
          response.message || response.error?.message || "Error en el registro";

        if (Object.keys(validationErrors).length > 0) {
          const formattedErrors = this.formatValidationErrors(validationErrors);
          if (formattedErrors) {
            errorMessage = `Errores de validación:\n${formattedErrors}`;
          }
        }

        return {
          success: false,
          message: errorMessage,
          error: response.error, // Estructura completa del error del servidor
          errors: validationErrors, // Errores de validación extraídos para fácil acceso
        };
      }
    } catch (error) {
      console.error("Registration error:", error);

      if (error.response?.data) {
        const errorData = error.response.data;
        const validationErrors = errorData.error?.data?.errors || {};

        let errorMessage = errorData.message || "Error en el registro";

        if (Object.keys(validationErrors).length > 0) {
          const formattedErrors = this.formatValidationErrors(validationErrors);
          if (formattedErrors) {
            errorMessage = `Errores de validación:\n${formattedErrors}`;
          }
        }

        return {
          success: false,
          message: errorMessage,
          errors: validationErrors,
          originalError: errorData,
        };
      }

      return {
        success: false,
        message: "Error de conexión. Inténtalo de nuevo.",
        error: error.message,
      };
    }
  }

  /**
   * Get display name for form fields
   * @param {string} fieldName - API field name
   * @returns {string} Display name in Spanish
   */
  getFieldDisplayName(fieldName) {
    const fieldNames = {
      firstName: "Nombre",
      lastName: "Apellido Paterno",
      maternal: "Apellido Materno",
      password: "Contraseña",
      phone: "Teléfono",
      dni: "DNI",
      email: "Email",
    };

    return fieldNames[fieldName] || fieldName;
  }

  /**
   * Format validation errors into user-friendly messages
   * @param {Object} errors - Validation errors from API
   * @returns {string} Formatted error message
   */
  formatValidationErrors(errors) {
    if (!errors || Object.keys(errors).length === 0) {
      return "";
    }

    const errorMessages = [];

    Object.keys(errors).forEach((field) => {
      const fieldErrors = errors[field];
      const fieldDisplayName = this.getFieldDisplayName(field);

      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach((error) => {
          // Limpiar y formatear el mensaje de error
          let cleanError = error
            .replace(/A\.\s*Materno/g, "Apellido Materno")
            .replace(/A\.\s*Paterno/g, "Apellido Paterno")
            .replace(/no puede ser nulo/g, "es obligatorio")
            .replace(
              /tiene caracteres no válidos/g,
              "contiene caracteres no permitidos"
            );

          errorMessages.push(`• ${fieldDisplayName}: ${cleanError}`);
        });
      }
    });

    return errorMessages.join("\n");
  }

  /**
   * Login a Club Mia member
   * @param {Object} credentials - Login credentials
   * @returns {Promise<Object>} Login response
   */
  async login(credentials) {
    try {
      // Prepare the data - identification_number and sian_domain (password commented out)
      const loginData = {
        identification_number: credentials.identification_number,
        // password: credentials.password, // Commented out - may be needed later
        sian_domain: process.env.REACT_APP_SIAN_DOMAIN,
      };

      console.log("Sending login data:", loginData);

      const response = await this.apiService.post(
        "/api/V1/club-mia/login",
        loginData
      );

      if (response.success) {
        console.log("Login successful:", response.data);

        if (response.data.data?.data?.token) {
          localStorage.setItem("clubMiaToken", response.data.data.data.token);
          localStorage.setItem(
            "clubMiaUser",
            JSON.stringify(response.data.data.data.user || {})
          );
          if (response.data.data?.data?.person) {
            localStorage.setItem(
              "clubMiaPerson",
              JSON.stringify(response.data.data.data.person)
            );
          }
        }

        return {
          success: true,
          message: "Inicio de sesión exitoso",
          data: response.data,
          user: response.data.data?.data?.user || {},
          person: response.data.data?.data?.person || {},
          token: response.data.data?.data?.token || null,
        };
      } else {
        console.error("Login failed:", response.error);
        return {
          success: false,
          message: response.error || "Credenciales incorrectas",
          errors: response.data?.errors || {},
        };
      }
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        message: "Error de conexión. Inténtalo de nuevo.",
        error: error.message,
      };
    }
  }

  /**
   * Get user profile information
   * @returns {Promise<Object>} Profile response
   */
  async getProfile() {
    try {
      const token = localStorage.getItem("clubMiaToken");

      if (!token) {
        return {
          success: false,
          message: "No hay sesión activa",
        };
      }

      const response = await this.apiService.get("/api/V1/club-mia/profile", {
        Authorization: `Bearer ${token}`,
      });

      if (response.success) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          message: response.error || "Error al obtener el perfil",
        };
      }
    } catch (error) {
      console.error("Profile error:", error);
      return {
        success: false,
        message: "Error de conexión. Inténtalo de nuevo.",
        error: error.message,
      };
    }
  }

  /**
   * Logout user
   */
  logout() {
    localStorage.removeItem("clubMiaToken");
    localStorage.removeItem("clubMiaUser");
    localStorage.removeItem("clubMiaPerson");
    console.log("User logged out");
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    const token = localStorage.getItem("clubMiaToken");
    return !!token;
  }

  /**
   * Get current user from localStorage
   * @returns {Object|null} Current user data
   */
  getCurrentUser() {
    const userStr = localStorage.getItem("clubMiaUser");
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  }

  /**
   * Get current token
   * @returns {string|null} Current token
   */
  getToken() {
    return localStorage.getItem("clubMiaToken");
  }

  /**
   * Get current person data from localStorage
   * @returns {Object|null} Current person data
   */
  getCurrentPerson() {
    const personStr = localStorage.getItem("clubMiaPerson");
    try {
      return personStr ? JSON.parse(personStr) : null;
    } catch (error) {
      console.error("Error parsing person data:", error);
      return null;
    }
  }
}

export default new ClubMiaAuthService();
